/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Settings", description = "Configure general Agents settings for your workspace, including business name, parent model, reviewers, and inference providers.", lastUpdatedDate = new Date(1782086400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Settings",
        description: "Configure general Agents settings for your workspace, including business name, parent model, reviewers, and inference providers.",
        lastUpdatedDate: "Mon Jun 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "General",
          "depth": 0,
          "id": "general"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      a: "a",
      ul: "ul",
      li: "li",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Settings control workspace-level configuration for Agents, including your business name, default parent model, reviewers, and inference providers."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Agents settings are accessible from ", _jsx(_components.strong, {
          children: "Agents"
        }), ", then ", _jsx(_components.strong, {
          children: "Settings"
        }), " in the left navigation. These settings apply to all agents across your workspace, including chats, reports, and custom agents."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Only users with the ", _jsx(_components.strong, {
            children: "Can this role configure Agents?"
          }), " permission can modify settings. See ", _jsx(_components.a, {
            href: "/agents/overview#data-governance",
            children: "Agents overview"
          }), " for more on permissions."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "general",
        children: "General"
      }), "\n", _jsx(_components.p, {
        children: "The Settings page includes four sections:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "General"
          }), " — configure the business name and select the default parent model the agent uses to query data. You can also select a parent model per chat from the chat input."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reviewers"
          }), " — assign user reviewers (workspace members) and agent reviewers (custom agents) who review agent-generated ", _jsx(_components.em, {
            children: "creative"
          }), " content before it's published"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Inference providers"
          }), " — select the AI model provider used by Agents. By default, agents use the Hightouch-managed provider. You can also connect your own provider (Google Vertex, Snowflake Cortex, or Databricks) so data stays within your infrastructure. Click ", _jsx(_components.strong, {
            children: "Add inference provider"
          }), " to connect an additional provider. This is an advanced setting that most organizations won't need to change."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Inference log"
          }), " — view the five most recent LLM interactions, including timestamp, duration, provider, and status. Use this to monitor agent activity and troubleshoot errors."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/settings/general.png",
          alt: "Settings General tab"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/settings/reviewers.png",
          alt: "Reviewers configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/settings/inference-provider.png",
          alt: "Inference providers"
        })
      })]
    });
  }
}
export default MDXContent;
